import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { FileDown } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import ProgramDay, { dayLabel, entryDomId } from './ProgramDay';
import ProgramPeople from './ProgramPeople';
import { daysOf, indexEntries, isEntry, pick, PrintContext } from './programModel';
import {
  AccentBar, Actions, DayTab, DayTabs, Deco, FilterBar, Notes, PdfButton, Scatter, Section, SectionInner, SectionTitle,
  TextButton,
} from './programStyles';
import lungFlower from '../../assets/images/program/lungflower.webp';
import blueFlower from '../../assets/images/program/flower.webp';
import blossoms from '../../assets/images/program/blossoms.webp';

const API = process.env.REACT_APP_API_URL || '';

// The flowers down the band, in the crew page's style: the lung flower, its bouquet of blossoms
// (blossoms.webp, cut out of lungflower.png) and the blue flower, mirrored, turned and sized so the
// repetition does not show. `top` is relative to the whole section (timeline and people grid).
const SCATTER = [
  { src: blossoms, side: 'left', top: '8%', width: 300, offset: -50, opacity: 0.95, mirror: true, rotate: -6, phone: true },
  { src: blueFlower, side: 'right', top: '15%', width: 250, offset: -40, opacity: 0.75, rotate: 10 },
  { src: lungFlower, side: 'left', top: '22%', width: 200, offset: -40, opacity: 0.7, mirror: true, rotate: 3 },
  { src: blossoms, side: 'right', top: '29%', width: 280, offset: -30, opacity: 0.9, rotate: 4 },
  { src: blueFlower, side: 'left', top: '36%', width: 230, offset: -50, opacity: 0.7, mirror: true, rotate: -14 },
  { src: blossoms, side: 'right', top: '43%', width: 240, offset: -40, opacity: 0.85, rotate: -10 },
  { src: lungFlower, side: 'left', top: '50%', width: 230, offset: -30, opacity: 0.8, mirror: true, rotate: -4, phone: true },
  { src: blueFlower, side: 'right', top: '58%', width: 220, offset: -50, opacity: 0.7, rotate: 18 },
  { src: blossoms, side: 'left', top: '65%', width: 270, offset: -40, opacity: 0.85, mirror: true, rotate: 6 },
  { src: lungFlower, side: 'right', top: '72%', width: 210, offset: -40, opacity: 0.75, rotate: 4, phone: true },
  { src: blossoms, side: 'left', top: '80%', width: 250, offset: -40, opacity: 0.8, mirror: true, rotate: -8 },
  { src: blueFlower, side: 'right', top: '88%', width: 240, offset: -40, opacity: 0.7, rotate: -6 },
];

// The ids of every entry in which this person is named (labels carry no person id).
const entriesOf = (program, personId) => {
  const ids = new Set();
  const walk = (nodes) => (nodes || []).forEach((n) => {
    if (!isEntry(n)) return;
    if ((n.people || []).some((p) => !p.tba && p.personId === personId)) ids.add(n.id);
    walk(n.children);
  });
  if (personId != null) walk(program?.entries);
  return ids;
};

// The printed name of a person id, from the people list or the program.
const nameOf = (program, people, personId) => {
  const listed = (people?.people || []).find((p) => p.personId === personId);
  if (listed) return listed.name;
  let found = '';
  const walk = (nodes) => (nodes || []).forEach((n) => {
    if (found || !isEntry(n)) return;
    const p = (n.people || []).find((x) => !x.tba && x.personId === personId);
    if (p) found = p.name;
    else walk(n.children);
  });
  walk(program?.entries);
  return found;
};

// The public program section (P10, plan §7.6), from data: `program` is the answer of
// GET /api/public/events/{slug}/program, `people` the answer of .../people (null: no people grid).
// It fetches nothing, so the admin preview (P11, decision 7) can render it with its own data;
// PublicProgramSection is the thin fetching wrapper for the public page.
// Renders nothing for a program without entries and without a public PDF. A program the admin
// published with only its PDF (P10a) has an empty tree: the title, the notes and the download button
// are then the whole section - no day tabs, no timeline, nothing about "no entries".
// print: the static version for the admin's PDF export (ProgramPrint): every day one after the other,
// names as text, no cards, buttons, tabs or decoration; `title` then heads the page.
const ProgramSectionView = ({
  program, people, language: languageProp, showTitle = true, print = false, title,
}) => {
  const intl = useIntl();
  const { language: siteLanguage } = useLanguage();
  const language = languageProp || siteLanguage;

  const days = useMemo(() => daysOf(program), [program]);
  const index = useMemo(() => indexEntries(program), [program]);
  const [dayIndex, setDayIndex] = useState(0);
  const [selection, setSelection] = useState(null); // a person id
  const highlighted = useMemo(() => entriesOf(program, selection), [program, selection]);

  const dayOfEntry = (id) => {
    const at = index.get(id);
    return at ? days.findIndex((d) => d.id === at.dayId) : -1;
  };

  const select = (personId) => {
    setSelection(personId);
    if (personId == null) return;
    const first = [...entriesOf(program, personId)].map(dayOfEntry).filter((i) => i >= 0).sort((a, b) => a - b)[0];
    if (first != null) setDayIndex(first);
  };

  // Jump to the first highlighted entry of the shown day once it is rendered.
  useEffect(() => {
    if (selection == null) return;
    const target = [...highlighted].find((id) => dayOfEntry(id) === dayIndex);
    const el = target != null ? document.getElementById(entryDomId(target)) : null;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, dayIndex]);

  // The PDF shows on its own: a program may have no public entry at all (P10a).
  // The PDF of the shown language: the server answers the English version to ?lang=en when there is one.
  const pdfUrl = program?.programPdf?.url ? `${API}${program.programPdf.url}?lang=${language === 'en' ? 'en' : 'de'}` : null;
  if (!program || (days.length === 0 && !pdfUrl)) return null;

  const currentDay = days.length > 0 ? days[Math.min(dayIndex, days.length - 1)] : null;
  const daysWithSelection = new Set([...highlighted].map(dayOfEntry));
  const notes = pick(language, program.event?.programNotesDe, program.event?.programNotesEn);
  const ctx = { highlighted, selection, onSelectPerson: print ? undefined : select, print };

  return (
    <PrintContext.Provider value={print}>
      <Section aria-labelledby={showTitle ? 'public-program-title' : undefined}>
        <Deco $variant="lung" src={lungFlower} alt="" aria-hidden="true" />
        {!print && SCATTER.map((f, i) => (
          <Scatter
            // eslint-disable-next-line react/no-array-index-key -- a fixed list
            key={i}
            src={f.src}
            alt=""
            aria-hidden="true"
            loading="lazy"
            $side={f.side}
            $top={f.top}
            $width={f.width}
            $offset={f.offset}
            $opacity={f.opacity}
            $mirror={f.mirror}
            $rotate={f.rotate}
            $phone={f.phone}
          />
        ))}
        <SectionInner>
          {showTitle && (
            <>
              <SectionTitle id="public-program-title">
                {title || intl.formatMessage({ id: 'program.public.title', defaultMessage: 'Program' })}
              </SectionTitle>
              <AccentBar />
            </>
          )}
          {notes && <Notes>{notes}</Notes>}
          {pdfUrl && !print && (
            <Actions>
              <PdfButton href={pdfUrl} target="_blank" rel="noopener noreferrer">
                <FileDown size={22} aria-hidden="true" />
                {intl.formatMessage({ id: 'program.public.pdf', defaultMessage: 'Program as PDF' })}
              </PdfButton>
            </Actions>
          )}

          {days.length > 1 && !print && (
            <DayTabs role="tablist" aria-label={intl.formatMessage({ id: 'program.public.days', defaultMessage: 'Program days' })}>
              {days.map((d, i) => (
                <DayTab
                  key={d.id ?? 'other'}
                  type="button"
                  role="tab"
                  aria-selected={i === dayIndex}
                  $active={i === dayIndex}
                  onClick={() => setDayIndex(i)}
                >
                  {dayLabel(intl, language, d) || intl.formatMessage({ id: 'program.public.noDay', defaultMessage: 'More' })}
                  {selection != null && daysWithSelection.has(i) ? ' •' : ''}
                </DayTab>
              ))}
            </DayTabs>
          )}

          {selection != null && (
            <FilterBar role="status">
              <span>
                {intl.formatMessage(
                  { id: 'program.public.filter', defaultMessage: 'Appearances of {name} are highlighted.' },
                  { name: nameOf(program, people, selection) },
                )}
              </span>
              <TextButton type="button" onClick={() => setSelection(null)}>
                {intl.formatMessage({ id: 'program.public.filter.clear', defaultMessage: 'Clear' })}
              </TextButton>
            </FilterBar>
          )}

          {print
            ? days.map((d) => (
              <ProgramDay key={d.id ?? 'other'} program={program} day={d} language={language} ctx={ctx} showHeading />
            ))
            : currentDay && (
              <ProgramDay program={program} day={currentDay} language={language} ctx={ctx} showHeading={days.length === 1} />
            )}

          {people && <ProgramPeople people={people} selection={selection} onSelectPerson={print ? undefined : select} />}
        </SectionInner>
      </Section>
    </PrintContext.Provider>
  );
};

export default ProgramSectionView;
